import type { PhaseId } from '../progress/model';
import type { TrackFactory } from './types';

export interface PhaseDefinition {
  id: PhaseId;
  order: number;
  name: string;
  summary: string;
  laps: number;
  /** Phase that must be completed first. Null means always open. */
  requires: PhaseId | null;
  implemented: boolean;
  load?: () => Promise<TrackFactory>;
}

export const PHASES: PhaseDefinition[] = [
  {
    id: 'cidade',
    order: 1,
    name: 'Cidade',
    summary: 'Avenidas, rampas e um estacionamento técnico. Asfalto com muita aderência.',
    laps: 3,
    requires: null,
    implemented: true,
    load: () => import('./city/CityTrack').then((mod) => mod.cityTrack),
  },
  {
    id: 'deserto',
    order: 2,
    name: 'Deserto',
    summary: 'Dunas, cânions e trechos de areia. Menos aderência fora do asfalto.',
    laps: 3,
    requires: 'cidade',
    implemented: true,
    load: () => import('./desert/DesertTrack').then((mod) => mod.desertTrack),
  },
  {
    id: 'neve',
    order: 3,
    name: 'Neve',
    summary: 'Montanhas, gelo e nevasca. Frear cedo: o gelo não perdoa.',
    laps: 3,
    requires: 'deserto',
    implemented: true,
    load: () => import('./snow/SnowTrack').then((mod) => mod.snowTrack),
  },
  {
    id: 'esgoto',
    order: 4,
    name: 'Esgoto',
    summary: 'Túneis estreitos, canos e poças sob luz industrial.',
    laps: 3,
    requires: 'neve',
    implemented: true,
    load: () => import('./sewer/SewerTrack').then((mod) => mod.sewerTrack),
  },
  {
    id: 'terra',
    order: 5,
    name: 'Terra',
    summary: 'Trilhas, lama, lombadas e saltos. O chassi vibra o caminho todo.',
    laps: 3,
    requires: 'esgoto',
    implemented: true,
    load: () => import('./dirt/DirtTrack').then((mod) => mod.dirtTrack),
  },
  {
    id: 'base',
    order: 6,
    name: 'Base de Lançamento',
    summary: 'Hangares, torres e a reta que aponta para o céu.',
    laps: 3,
    requires: 'terra',
    implemented: true,
    load: () => import('./launch/LaunchTrack').then((mod) => mod.launchTrack),
  },
  {
    id: 'espaco',
    order: 7,
    name: 'Espaço',
    summary: 'Uma estrada em volta da Terra, entre estrelas e uma nebulosa azul.',
    laps: 3,
    requires: 'base',
    implemented: true,
    load: () => import('./space/SpaceTrack').then((mod) => mod.spaceTrack),
  },
];

export function phaseById(id: PhaseId): PhaseDefinition {
  const phase = PHASES.find((entry) => entry.id === id);
  if (!phase) throw new Error(`Fase desconhecida: ${id}`);
  return phase;
}

export function isUnlocked(phase: PhaseDefinition, completed: (id: PhaseId) => boolean): boolean {
  if (!phase.requires) return true;
  return completed(phase.requires);
}

export function lockReason(phase: PhaseDefinition, completed: (id: PhaseId) => boolean): string | null {
  if (!isUnlocked(phase, completed)) {
    const previous = PHASES.find((entry) => entry.id === phase.requires);
    return `Conclua a fase ${previous?.name ?? 'anterior'} para desbloquear`;
  }
  if (!phase.implemented) return 'Em breve';
  return null;
}

export function nextPlayable(completed: (id: PhaseId) => boolean): PhaseId {
  for (const phase of PHASES) {
    if (!phase.implemented || !phase.load) continue;
    if (isUnlocked(phase, completed) && !completed(phase.id)) return phase.id;
  }
  return 'cidade';
}
